// @ts-check
/** @typedef {import('@datahooks/types/tagging.types').ProjectTagValuesResponse} ProjectTagValuesResponse */

import axios from 'axios';

import { useAuthenticatedQuery } from '@datahooks/useAuthenticatedQuery';
import { useOrgContext } from '@datahooks/admin/organisation/AdminOrgIDContext';
import useUser from '@datahooks/useUser';

import { API_ADMIN_URL, FUNCTION_KEY, PROJECT_SCOPE } from '@home/config';

import logError from '@datahooks/metadata/ErrorLogging';

/**
 * @typedef {Object} UseProjectTagsResult
 * @property {ProjectTagValuesResponse} projectTagValues
 * @property {boolean} isLoading
 * @property {boolean} isPreviousData - The previous organisation's values are shown while the next fetch runs
 * @property {() => Promise<any>} refetch
 * @property {any} error
*/

/**
 * Fetch the latest coverage for a project.
 * 
 * @returns {UseProjectTagsResult}
*/
export default function useProjectTagValues() {
    const { organisationData } = useOrgContext();
    const { user } = useUser();

    const orgId = organisationData?.id ?? null;
    const userId = user?.id ?? null;

    /**
     * Function to fetch project tag values from the API
     * 
     * @param {string} token 
     * @returns {Promise<ProjectTagValuesResponse>} The project tag values
     */
    const fetchCoverage = async (token) => {
       
        const params = { org_id: orgId };

        try {
            const res = await axios.get(`${API_ADMIN_URL}project_tag_values`, {
                headers: {
                Authorization: `Bearer ${token}`,
                'x-functions-key': FUNCTION_KEY,
                },
                params,
            });
            return res.data;

        } catch (error) {
            logError('Failed to load project tags in useProjectTagValues', error, { orgId, userId });
            throw error;
        }
    };

    const queryKey = ['project_tag_values', orgId, userId];
    const scopes = [PROJECT_SCOPE];

    /** @type {ProjectTagValuesResponse} */
    const emptyProjectTagValues = [];
    const {
        data: projectTagValues = emptyProjectTagValues,
        isLoading,
        refetch,
        error,
        isPreviousData,
    } = useAuthenticatedQuery(queryKey, fetchCoverage, scopes, {
        enabled: orgId !== null && userId !== null,
        staleTime: 60000, // 1 minute
        keepPreviousData: true,
    });

    return { projectTagValues, isLoading, isPreviousData, refetch, error };
}